import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Download, FileText, Loader2, ReceiptText } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const fmt = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

export default function MyInvoices() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState(null);
  useEffect(() => { api.get("/billing/history").then(({ data }) => setDocs(data.documents)).catch(() => setDocs("error")); }, []);
  return (
    <div className="min-h-screen bg-[#F8FAFC] font-parent text-slate-900">
      <main className="max-w-3xl mx-auto px-5 py-10">
        <Button variant="ghost" className="rounded-full -ml-3" onClick={() => navigate("/parent")} data-testid="invoices-back"><ArrowLeft size={16} className="mr-1" /> Back</Button>
        <h1 className="font-kids text-4xl sm:text-5xl font-bold mt-4">My invoices</h1>
        <p className="text-slate-600 mt-2">Mes factures et avoirs — download any document as a PDF.</p>
        <div className="mt-8 space-y-3" data-testid="invoices-list">
          {!docs ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-blue-600" /></div>
            : docs === "error" ? <p className="nb-card bg-white p-6 text-center text-red-600" data-testid="invoices-error">Could not load your invoices. Please refresh the page.</p>
            : !docs.length ? <p className="nb-card bg-white p-6 text-center text-slate-500" data-testid="invoices-empty">No invoices yet. They appear here once your payment is confirmed.</p>
            : docs.map((d, i) => <Doc key={d.type + d.id} d={d} i={i} />)}
        </div>
      </main>
    </div>
  );
}

function Doc({ d, i }) {
  const [busy, setBusy] = useState(false);
  const credit = d.type === "credit";
  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(credit ? `/billing/credit-note/${d.id}` : `/billing/invoice/${d.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      Object.assign(document.createElement("a"), { href: url, download: `${credit ? "Credit-note" : "Invoice"}-${d.number}.pdf` }).click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the document. Please try again."); } finally { setBusy(false); }
  };
  const Icon = credit ? ReceiptText : FileText;
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
      className="nb-card bg-white p-4 flex flex-wrap items-center gap-4" data-testid={`invoice-doc-${d.number}`}>
      <div className={`w-11 h-11 rounded-xl border-2 border-slate-900 flex items-center justify-center ${credit ? "bg-rose-100" : "bg-blue-100"}`}><Icon size={20} /></div>
      <div className="flex-1 min-w-[180px]">
        <p className="font-bold">{credit ? "Credit note / Avoir" : "Invoice / Facture"} {d.number}</p>
        <p className="text-sm text-slate-500">{fmt(d.date)} · {credit ? `For invoice ${d.invoice_no || "-"} · ${d.reason}` : d.children.join(", ")}</p>
        {!credit && d.discount_reason && <p className="text-xs text-green-700 mt-0.5">{d.discount_reason}</p>}
      </div>
      <span className={`text-lg font-bold ${credit ? "text-rose-600" : ""}`} data-testid={`invoice-amount-${d.number}`}>{credit ? `− Rs ${-d.amount}` : `Rs ${d.amount}`}</span>
      <Button onClick={download} disabled={busy} variant="outline" className="rounded-full border-2 border-slate-900" data-testid={`invoice-download-${d.number}`}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} className="mr-1" />} PDF
      </Button>
    </motion.div>
  );
}
